import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button, Modal } from "react-bootstrap";
import dayjs from "dayjs";
import { API_BASE_URL } from "./config";
import AuthenticatedAudio from "./AuthenticatedAudio";

const AUDIOS_PER_PAGE = 10;

const groupResumosByDate = (resumos) => {
    return (Array.isArray(resumos) ? resumos : []).reduce((acc, resumo) => {
        const dataFormatada = dayjs(resumo.cp_res_data).format("DD/MM/YYYY");
        if (!acc[dataFormatada]) {
            acc[dataFormatada] = [];
        }
        acc[dataFormatada].push(resumo);
        return acc;
    }, {});
};

const sortResumoDates = (dates) => {
    return [...dates].sort((a, b) => {
        const dateA = new Date(a.split("/").reverse().join("-"));
        const dateB = new Date(b.split("/").reverse().join("-"));
        return dateB - dateA;
    });
};

const formatDateBR = (date) => {
    if (!date) return "-";

    if (typeof date === "string") {
        const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
    }

    const parsedDate = dayjs(date);
    return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const getFileExtension = (fileUrl) => {
    if (!fileUrl || typeof fileUrl !== "string") return "";

    const cleanUrl = fileUrl.split("?")[0].split("#")[0];
    const fileName = cleanUrl.split("/").pop() || "";
    const extension = fileName.includes(".") ? fileName.split(".").pop() : "";

    return extension.toLowerCase();
};

const isPdfFile = (fileUrl) => getFileExtension(fileUrl) === "pdf";

const normalizeFileUrl = (fileUrl, defaultFolder = "") => {
    if (!fileUrl || typeof fileUrl !== "string") return "";
    if (!defaultFolder) return fileUrl;

    const normalizedFolder = defaultFolder.replace(/^\/+|\/+$/g, "");

    try {
        const parsed = new URL(fileUrl);
        const pathname = decodeURIComponent(parsed.pathname);
        const hasFolder = pathname.includes(`/${normalizedFolder}/`);

        if (!hasFolder && /^\/[^/]+$/.test(pathname)) {
            parsed.pathname = `/${normalizedFolder}${pathname}`;
            return parsed.toString();
        }

        return fileUrl;
    } catch {
        const cleanPath = fileUrl.replace(/^\/+/, "");
        return cleanPath.includes("/")
            ? fileUrl
            : `${API_BASE_URL}/${normalizedFolder}/${cleanPath}`;
    }
};

const getResumoMateriaisAluno = (resumo) => {
    const materiais = [];

    if (resumo.cp_res_arquivo) {
        const extension = getFileExtension(resumo.cp_res_arquivo);
        const isPdf = extension === "pdf";

        materiais.push({
            href: resumo.cp_res_arquivo,
            label: isPdf ? "PDF da aula" : "Arquivo da aula",
            helper: isPdf ? "Visualizar PDF" : extension ? `Abrir arquivo .${extension}` : "Abrir arquivo",
            icon: isPdf ? "solar:file-text-bold" : "solar:file-download-bold",
            accentClass: "bg-primary-subtle text-primary",
            type: "file",
            defaultFolder: "materialdeaula",
        });
    }

    if (resumo.cp_res_link) {
        materiais.push({
            href: resumo.cp_res_link,
            label: "Link da aula",
            helper: "Acessar link",
            icon: "solar:link-circle-bold",
            accentClass: "bg-info-subtle text-info",
            type: "link",
        });
    }

    if (resumo.cp_res_link_youtube) {
        materiais.push({
            href: resumo.cp_res_link_youtube,
            label: "Video da aula",
            helper: "Assistir video",
            icon: "logos:youtube-icon",
            accentClass: "bg-danger-subtle text-danger",
            type: "link",
        });
    }

    return materiais;
};

const SalaDeAulaAlunoLayout = () => {
    const [turmas, setTurmas] = useState([]);
    const [audioStatus, setAudioStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [paginasAudios, setPaginasAudios] = useState({});
    const [resumosAbertos, setResumosAbertos] = useState({});
    const [showPDF, setShowPDF] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    const handleViewPDF = (url, defaultFolder = "") => {
        setPdfUrl(normalizeFileUrl(url, defaultFolder));
        setShowPDF(true);
    };

    const handleOpenFile = (url, defaultFolder = "") => {
        const normalizedUrl = normalizeFileUrl(url, defaultFolder);

        if (isPdfFile(url)) {
            handleViewPDF(normalizedUrl);
            return;
        }

        window.open(normalizedUrl, "_blank", "noopener,noreferrer");
    };

    const handleClosePDF = () => {
        setShowPDF(false);
        setPdfUrl("");
    };

    useEffect(() => {
        let isMounted = true;

        const fetchJson = async (url) => {
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                throw new Error(`Falha ao carregar ${url}`);
            }

            return response.json();
        };

        const fetchOptionalJson = async (url) => {
            try {
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) {
                    return null;
                }

                return response.json();
            } catch (fetchError) {
                console.error(`Erro ao buscar ${url}:`, fetchError);
                return null;
            }
        };

        const loadTurmas = async () => {
            if (!token) {
                if (isMounted) {
                    setTurmas([]);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError("");

            try {
                const [turmasResponse, audiosMarcadosResponse] = await Promise.all([
                    fetchJson(`${API_BASE_URL}/turmasComAlunos`),
                    userId
                        ? fetchOptionalJson(`${API_BASE_URL}/audios-marcados/${userId}`)
                        : Promise.resolve([]),
                ]);

                const turmasUnicas = [];
                const turmaIds = new Set();

                for (const turma of Array.isArray(turmasResponse) ? turmasResponse : []) {
                    const turmaId = Number(turma?.cp_tr_id);
                    if (!Number.isFinite(turmaId) || turmaIds.has(turmaId)) {
                        continue;
                    }
                    turmaIds.add(turmaId);
                    turmasUnicas.push(turma);
                }

                const marcados = (Array.isArray(audiosMarcadosResponse) ? audiosMarcadosResponse : []).reduce((acc, audioId) => {
                    acc[audioId] = true;
                    return acc;
                }, {});

                const cursoIds = [
                    ...new Set(
                        turmasUnicas
                            .map((turma) => Number(turma.cp_tr_curso_id))
                            .filter((cursoId) => Number.isFinite(cursoId) && cursoId > 0)
                    ),
                ];

                const cursosMap = new Map(
                    await Promise.all(
                        cursoIds.map(async (cursoId) => {
                            const [materiaisResponse, audiosResponse] = await Promise.all([
                                fetchOptionalJson(`${API_BASE_URL}/curso-material/${cursoId}`),
                                fetchOptionalJson(`${API_BASE_URL}/audios-curso/${cursoId}`),
                            ]);

                            const audiosOrdenados = Array.isArray(audiosResponse)
                                ? [...audiosResponse].sort((a, b) =>
                                      String(a.cp_nome_audio || "").localeCompare(
                                          String(b.cp_nome_audio || ""),
                                          undefined,
                                          { numeric: true, sensitivity: "base" }
                                      )
                                  )
                                : [];

                            return [
                                cursoId,
                                {
                                    materiais: materiaisResponse,
                                    audios: audiosOrdenados,
                                },
                            ];
                        })
                    )
                );

                const turmasComConteudo = await Promise.all(
                    turmasUnicas.map(async (turma) => {
                        const turmaId = Number(turma.cp_tr_id);
                        const cursoId = Number(turma.cp_tr_curso_id);
                        const cursoData = cursosMap.get(cursoId) || { materiais: null, audios: [] };
                        const resumosResponse = await fetchOptionalJson(`${API_BASE_URL}/resumos/${turmaId}`);
                        const resumos = groupResumosByDate(resumosResponse || []);
                        const audios = cursoData.audios;
                        const audiosOuvidos = audios.filter((audio) => marcados[audio.cp_audio_id]).length;
                        const progressoAudios = audios.length > 0 ? Math.round((audiosOuvidos / audios.length) * 100) : 0;

                        return {
                            ...turma,
                            materiais: cursoData.materiais,
                            audios,
                            resumos,
                            progressoAudios,
                        };
                    })
                );

                if (!isMounted) {
                    return;
                }

                setAudioStatus(marcados);
                setTurmas(turmasComConteudo);
                setPaginasAudios(
                    turmasComConteudo.reduce((acc, turma) => {
                        acc[turma.cp_tr_id] = 1;
                        return acc;
                    }, {})
                );
            } catch (loadError) {
                console.error("Erro ao carregar sala do aluno:", loadError);
                if (isMounted) {
                    setTurmas([]);
                    setError("Nao foi possivel carregar as turmas do aluno.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadTurmas();

        return () => {
            isMounted = false;
        };
    }, [token, userId]);

    const handleAudioPlay = async (audioId) => {
        if (!userId || audioStatus[audioId]) {
            return;
        }

        try {
            await fetch(`${API_BASE_URL}/registrar-visualizacao`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ userId, audioId }),
            });

            setAudioStatus((prevStatus) => ({
                ...prevStatus,
                [audioId]: true,
            }));

            setTurmas((prevTurmas) =>
                prevTurmas.map((turma) => {
                    const audiosOuvidos = turma.audios.filter(
                        (audio) => audio.cp_audio_id === audioId || audioStatus[audio.cp_audio_id]
                    ).length;

                    return {
                        ...turma,
                        progressoAudios:
                            turma.audios.length > 0
                                ? Math.round((audiosOuvidos / turma.audios.length) * 100)
                                : 0,
                    };
                })
            );
        } catch (playError) {
            console.error("Erro ao registrar visualizacao de audio:", playError);
        }
    };

    const toggleResumo = (turmaId, data) => {
        const resumoKey = `${turmaId}-${data}`;
        setResumosAbertos((prevState) => ({
            ...prevState,
            [resumoKey]: !prevState[resumoKey],
        }));
    };

    const changePaginaAudios = (turmaId, page) => {
        setPaginasAudios((prevState) => ({
            ...prevState,
            [turmaId]: page,
        }));
    };

    return (
        <>
            <style>{`
                .sala-aluno-shell {
                    background: #f3f4f7;
                    border-radius: 16px;
                }

                .sala-aluno-header {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
                    background: transparent;
                }

                .sala-turma-card {
                    background: #ffffff;
                    border: 1px solid rgba(15, 23, 42, 0.08);
                    border-radius: 18px;
                    overflow: hidden;
                    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
                }

                .sala-turma-hero {
                    background: linear-gradient(135deg, #e8eefc 0%, #f8fbff 100%);
                    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
                }

                .sala-aluno-grid {
                    display: grid;
                    grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
                }

                .sala-aluno-side {
                    background: #eef2f8;
                    padding: 24px;
                }

                .sala-aluno-main {
                    background: #f8f9fb;
                    padding: 24px;
                }

                .sala-section {
                    background: #ffffff;
                    border-radius: 14px;
                    padding: 16px;
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
                }

                .sala-section + .sala-section {
                    margin-top: 20px;
                }

                .material-card {
                    transition: all 0.2s;
                    border: none;
                    border-radius: 12px;
                    background: #eef1f6;
                    color: inherit;
                    cursor: pointer;
                    text-align: left;
                    width: 100%;
                }

                .material-card:hover {
                    background-color: #e6ebf3;
                }

                .audio-card {
                    border-radius: 16px !important;
                    background: #ffffff !important;
                    border: none !important;
                    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
                }

                .audio-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: var(--text-primary-light) !important;
                    line-height: 1.4;
                }

                .resumo-toggle {
                    border: none;
                    border-radius: 12px !important;
                    background: #eef1f6;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--text-primary-light);
                }

                .resumo-toggle:not(.collapsed) {
                    background: #e6ebf3;
                    color: #1d4ed8;
                }

                .resumo-body {
                    padding: 12px 6px 4px;
                }

                .resumo-item {
                    background: #f5f7fb;
                    border: none;
                    border-radius: 12px;
                    transition: all 0.2s;
                    color: inherit;
                    cursor: pointer;
                }

                .resumo-item:hover {
                    background-color: #eef1f6;
                }

                [data-theme="dark"] .sala-aluno-shell {
                    background: var(--dark-1, #0f1115) !important;
                    color: var(--text-primary-light, #ffffff);
                }

                [data-theme="dark"] .sala-aluno-shell > .card-body,
                [data-theme="dark"] .sala-aluno-header {
                    background: var(--dark-2, #1a1d21) !important;
                    border-color: var(--dark-3, #2c3036) !important;
                }

                [data-theme="dark"] .sala-turma-card {
                    background: var(--dark-2, #1a1d21) !important;
                    border-color: var(--dark-3, #2c3036);
                    box-shadow: none;
                }

                [data-theme="dark"] .sala-turma-hero {
                    background: #252f3f;
                    border-color: var(--dark-3, #2c3036);
                }

                [data-theme="dark"] .sala-aluno-side {
                    background: #161c26;
                }

                [data-theme="dark"] .sala-aluno-main {
                    background: #121821;
                }

                [data-theme="dark"] .sala-section,
                [data-theme="dark"] .audio-card {
                    background: var(--dark-2, #1a1d21) !important;
                    border: 1px solid var(--dark-3, #2c3036) !important;
                    box-shadow: none;
                }

                [data-theme="dark"] .material-card,
                [data-theme="dark"] .resumo-toggle,
                [data-theme="dark"] .resumo-item,
                [data-theme="dark"] .sala-empty-state {
                    background: var(--dark-3, #2c3036) !important;
                    color: var(--text-primary-light, #ffffff) !important;
                }

                [data-theme="dark"] .material-card:hover,
                [data-theme="dark"] .resumo-toggle:not(.collapsed),
                [data-theme="dark"] .resumo-item:hover {
                    background: var(--dark-4, #343a40) !important;
                }

                [data-theme="dark"] .sala-aluno-shell .bg-base {
                    background-color: var(--dark-3, #2c3036) !important;
                    border-color: var(--dark-4, #343a40) !important;
                }

                [data-theme="dark"] .sala-aluno-shell .text-primary-light,
                [data-theme="dark"] .audio-title,
                [data-theme="dark"] .sala-empty-state-title {
                    color: var(--text-primary-light, #ffffff) !important;
                }

                [data-theme="dark"] .sala-aluno-shell .text-secondary,
                [data-theme="dark"] .sala-aluno-shell .text-secondary-light,
                [data-theme="dark"] .sala-aluno-shell .text-muted {
                    color: var(--text-secondary-light, #adb5bd) !important;
                }

                [data-theme="dark"] .sala-aluno-shell .bg-secondary-subtle {
                    background-color: rgba(173, 181, 189, 0.14) !important;
                    color: var(--text-secondary-light, #adb5bd) !important;
                }

                [data-theme="dark"] .sala-progress {
                    background-color: var(--dark-3, #2c3036) !important;
                }

                [data-theme="dark"] .sala-empty-state-icon {
                    background: var(--dark-2, #1a1d21) !important;
                }

                [data-theme="dark"] .resumo-toggle::after {
                    filter: invert(1) grayscale(100%) brightness(180%);
                }

                @media (max-width: 992px) {
                    .sala-aluno-grid {
                        grid-template-columns: 1fr;
                    }

                    .sala-aluno-side,
                    .sala-aluno-main {
                        padding: 20px;
                    }
                }
            `}</style>

            <div className="card h-100 p-0 radius-12 border-0 bg-base sala-aluno-shell">
                <div className="card-header border-bottom bg-base py-3 px-4 d-flex align-items-center justify-content-between sala-aluno-header">
                    <h5 className="mb-0 fw-bold text-primary-light">Sala de Aula</h5>
                </div>

                <div className="card-body p-4">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                            <p className="mt-3 mb-0 text-secondary-light fw-medium">Carregando turmas do aluno...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-warning mb-0">{error}</div>
                    ) : turmas.length === 0 ? (
                        <div className="text-center py-5">
                            <Icon icon="solar:notebook-minimalistic-linear" width="52" className="text-secondary opacity-50 mb-3" />
                            <h5 className="text-primary-light fw-bold">Nenhuma turma encontrada</h5>
                            <p className="text-secondary-light mb-0">O aluno nao possui turmas vinculadas para exibir nesta tela.</p>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-4">
                            {turmas.map((turma) => {
                                const turmaId = Number(turma.cp_tr_id);
                                const totalPaginasAudios = Math.max(1, Math.ceil(turma.audios.length / AUDIOS_PER_PAGE));
                                const paginaAtual = Math.min(paginasAudios[turmaId] || 1, totalPaginasAudios);
                                const audiosPaginados = turma.audios.slice(
                                    (paginaAtual - 1) * AUDIOS_PER_PAGE,
                                    paginaAtual * AUDIOS_PER_PAGE
                                );
                                const materiaisAulaPorData = Object.entries(turma.resumos).reduce((acc, [data, resumos]) => {
                                    const materiaisDaData = resumos.flatMap((resumo) =>
                                        getResumoMateriaisAluno(resumo).map((material, materialIndex) => ({
                                            ...material,
                                            id: `${resumo.cp_res_id}-${materialIndex}`,
                                        }))
                                    );

                                    if (materiaisDaData.length > 0) {
                                        acc[data] = materiaisDaData;
                                    }

                                    return acc;
                                }, {});
                                const resumoDates = sortResumoDates(Object.keys(materiaisAulaPorData));
                                const materiais = turma.materiais || {};
                                const hasMaterial =
                                    Boolean(materiais.cp_youtube_link_curso) ||
                                    [1, 2, 3].some((num) => Boolean(materiais[`cp_pdf${num}_curso`]));

                                return (
                                    <section key={turmaId} className="sala-turma-card">
                                        <div className="sala-turma-hero px-4 py-3">
                                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                                                <div>
                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                        <h5 className="mb-0 fw-bold text-primary-light">{turma.cp_tr_nome}</h5>
                                                        {Number(turma.cp_tr_inativa) === 1 && (
                                                            <span className="badge bg-secondary">Inativa</span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-3 mt-2 text-secondary-light small">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <Icon icon="solar:user-bold" className="text-primary" />
                                                            {turma.nomeDoProfessor || "Professor nao informado"}
                                                        </span>
                                                        <span className="d-flex align-items-center gap-1">
                                                            <Icon icon="solar:buildings-bold" className="text-primary" />
                                                            {turma.nomeDaEscola || "Escola nao informada"}
                                                        </span>
                                                        <span className="d-flex align-items-center gap-1">
                                                            <Icon icon="solar:calendar-date-bold" className="text-primary" />
                                                            Inicio: {formatDateBR(turma.cp_tr_data)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="sala-aluno-grid">
                                            <div className="sala-aluno-side">
                                                <div className="sala-section">
                                                    <h6 className="fw-bold mb-4 d-flex align-items-center gap-2 text-primary">
                                                        <Icon icon="solar:folder-with-files-bold" /> Materiais do Curso
                                                    </h6>

                                                    {hasMaterial ? (
                                                        <div className="d-flex flex-column gap-3">
                                                            {materiais.cp_youtube_link_curso && (
                                                                <a
                                                                    href={materiais.cp_youtube_link_curso}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="material-card p-3 text-decoration-none d-flex align-items-center gap-3 bg-base"
                                                                >
                                                                    <div className="bg-danger-subtle text-danger rounded-circle p-2 d-flex">
                                                                        <Icon icon="logos:youtube-icon" width="20" />
                                                                    </div>
                                                                    <div>
                                                                        <span className="d-block fw-semibold text-primary-light">Video introdutorio</span>
                                                                        <span className="text-xs text-secondary-light">Assistir no YouTube</span>
                                                                    </div>
                                                                </a>
                                                            )}

                                                            {[1, 2, 3].map((num) => {
                                                                const pdfKey = `cp_pdf${num}_curso`;
                                                                return materiais[pdfKey] ? (
                                                                    <button
                                                                        key={pdfKey}
                                                                        type="button"
                                                                        onClick={() => handleOpenFile(materiais[pdfKey])}
                                                                        className="material-card p-3 text-decoration-none d-flex align-items-center gap-3 bg-base"
                                                                    >
                                                                        <div className="bg-primary-subtle text-primary rounded-circle p-2 d-flex">
                                                                            <Icon icon="solar:file-text-bold" width="20" />
                                                                        </div>
                                                                        <div>
                                                                            <span className="d-block fw-semibold text-primary-light">Material complementar {num}</span>
                                                                            <span className="text-xs text-secondary-light">Baixar PDF</span>
                                                                        </div>
                                                                        <Icon icon="solar:download-linear" className="ms-auto text-secondary-light" width="20" />
                                                                    </button>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4 text-muted">
                                                            <Icon icon="solar:file-smile-linear" width="32" className="mb-2 opacity-50" />
                                                            <p className="mb-0 text-sm">Nenhum material disponivel para esta turma.</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="sala-section">
                                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-primary">
                                                        <Icon icon="solar:notebook-bookmark-bold" /> Materiais da Aula
                                                    </h6>

                                                    <div className="accordion d-flex flex-column gap-2">
                                                        {resumoDates.length > 0 ? (
                                                            resumoDates.map((data) => {
                                                                const resumoKey = `${turmaId}-${data}`;
                                                                const resumoAberto = Boolean(resumosAbertos[resumoKey]);

                                                                return (
                                                                    <div className="accordion-item border-0 bg-transparent" key={resumoKey}>
                                                                        <h2 className="accordion-header">
                                                                            <button
                                                                                className={`accordion-button resumo-toggle shadow-none px-4 py-2 ${resumoAberto ? "" : "collapsed"}`}
                                                                                type="button"
                                                                                onClick={() => toggleResumo(turmaId, data)}
                                                                            >
                                                                                <Icon icon="solar:calendar-date-bold" className="me-2 text-primary" /> {data}
                                                                            </button>
                                                                        </h2>
                                                                        <div className={`accordion-collapse collapse ${resumoAberto ? "show" : ""}`}>
                                                                            <div className="accordion-body resumo-body">
                                                                                {materiaisAulaPorData[data].map((material) => (
                                                                                    material.type === "file" ? (
                                                                                        <button
                                                                                            key={material.id}
                                                                                            type="button"
                                                                                            onClick={() => handleOpenFile(material.href, material.defaultFolder)}
                                                                                            className="resumo-item p-3 mb-2 text-decoration-none d-flex align-items-center gap-3 w-100 text-start"
                                                                                        >
                                                                                            <div className={`${material.accentClass} rounded-circle p-2 d-flex`}>
                                                                                                <Icon icon={material.icon} width="20" />
                                                                                            </div>
                                                                                            <div>
                                                                                                <span className="d-block fw-semibold text-primary-light">{material.label}</span>
                                                                                                <span className="text-xs text-secondary-light">{material.helper}</span>
                                                                                            </div>
                                                                                            <Icon icon="solar:arrow-right-up-linear" className="ms-auto text-secondary-light" width="20" />
                                                                                        </button>
                                                                                    ) : (
                                                                                        <a
                                                                                            key={material.id}
                                                                                            href={material.href}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="resumo-item p-3 mb-2 text-decoration-none d-flex align-items-center gap-3"
                                                                                        >
                                                                                            <div className={`${material.accentClass} rounded-circle p-2 d-flex`}>
                                                                                                <Icon icon={material.icon} width="20" />
                                                                                            </div>
                                                                                            <div>
                                                                                                <span className="d-block fw-semibold text-primary-light">{material.label}</span>
                                                                                                <span className="text-xs text-secondary-light">{material.helper}</span>
                                                                                            </div>
                                                                                            <Icon icon="solar:arrow-right-up-linear" className="ms-auto text-secondary-light" width="20" />
                                                                                        </a>
                                                                                    )
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <div className="text-center py-4 text-muted">
                                                                <Icon icon="solar:clipboard-remove-linear" width="32" className="mb-2 opacity-50" />
                                                                <p className="mb-0 text-sm">Nenhum material de aula disponivel para esta turma.</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="sala-aluno-main">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <label className="fw-bold text-primary-light d-flex align-items-center gap-2">
                                                        <Icon icon="solar:chart-2-bold" className="text-primary" /> Progresso do Curso
                                                    </label>
                                                    <span className="badge bg-success-subtle text-success fw-bold px-3 py-2 rounded-pill">
                                                        {turma.progressoAudios}% concluido
                                                    </span>
                                                </div>

                                                <div className="progress sala-progress mb-4" style={{ height: "12px", borderRadius: "10px", backgroundColor: "#e9ecef" }}>
                                                    <div
                                                        className="progress-bar bg-gradient-success"
                                                        role="progressbar"
                                                        style={{
                                                            width: `${turma.progressoAudios}%`,
                                                            borderRadius: "10px",
                                                            background: "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                                                        }}
                                                    />
                                                </div>

                                                {audiosPaginados.length > 0 ? (
                                                    <>
                                                        <div className="d-flex flex-column gap-4">
                                                            {audiosPaginados.map((audio) => (
                                                                <div key={audio.cp_audio_id} className="audio-card card p-0">
                                                                    <div className="card-body p-4">
                                                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                                                            <h6 className="audio-title mb-0 flex-grow-1 pe-3 d-flex align-items-center gap-3" title={audio.cp_nome_audio}>
                                                                                <span className="bg-primary-subtle text-primary rounded-circle p-1 d-flex align-items-center justify-content-center" style={{ width: "24px", height: "24px" }}>
                                                                                    <Icon icon="solar:music-note-bold" width="14" />
                                                                                </span>
                                                                                {audio.cp_nome_audio}
                                                                            </h6>

                                                                            <div
                                                                                className={`d-flex align-items-center gap-1 px-2 py-1 rounded-pill ${
                                                                                    audioStatus[audio.cp_audio_id]
                                                                                        ? "bg-success-subtle text-success"
                                                                                        : "bg-secondary-subtle text-secondary"
                                                                                }`}
                                                                            >
                                                                                <Icon
                                                                                    icon={audioStatus[audio.cp_audio_id] ? "solar:check-circle-bold" : "solar:circle-linear"}
                                                                                    width="18"
                                                                                />
                                                                                <span className="text-xs fw-bold d-none d-md-inline">
                                                                                    {audioStatus[audio.cp_audio_id] ? "Ouvido" : "Pendente"}
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        <AuthenticatedAudio
                                                                            url={`${API_BASE_URL}/audios/${audio.cp_nome_audio}`}
                                                                            token={token}
                                                                            onPlay={() => handleAudioPlay(audio.cp_audio_id)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {totalPaginasAudios > 1 && (
                                                            <div className="d-flex align-items-center justify-content-between mt-4 flex-wrap gap-3">
                                                                <span className="text-secondary-light">
                                                                    Pagina {paginaAtual} de {totalPaginasAudios}
                                                                </span>

                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => changePaginaAudios(turmaId, Math.max(paginaAtual - 1, 1))}
                                                                        disabled={paginaAtual === 1}
                                                                    >
                                                                        Anterior
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => changePaginaAudios(turmaId, Math.min(paginaAtual + 1, totalPaginasAudios))}
                                                                        disabled={paginaAtual === totalPaginasAudios}
                                                                    >
                                                                        Proximo
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="sala-empty-state text-center p-5 rounded-4 border border-secondary-subtle">
                                                        <div className="sala-empty-state-icon bg-white rounded-circle p-4 d-inline-flex mb-3">
                                                            <Icon icon="solar:music-note-slider-linear" width="48" className="text-secondary opacity-50" />
                                                        </div>
                                                        <h5 className="sala-empty-state-title fw-bold mb-2">Nenhum audio encontrado</h5>
                                                        <p className="text-secondary mb-0">Nao ha audios disponiveis para esta turma no momento.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                show={showPDF}
                onHide={handleClosePDF}
                centered
                fullscreen
                className="bg-dark text-white material-pdf-modal"
            >
                <Modal.Header className="bg-dark border-0 d-flex justify-content-end p-2">
                    <Button
                        variant="light"
                        onClick={handleClosePDF}
                        className="btn-close-white material-modal-close"
                    >
                        X
                    </Button>
                </Modal.Header>
                <Modal.Body className="p-0 material-modal-body">
                    {pdfUrl && (
                        <iframe
                            src={`https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            title="PDF Viewer"
                            className="material-modal-iframe"
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SalaDeAulaAlunoLayout;
